// MIDAS V3 — Citation chip (Phase 2)
//
// Ports `Cite` from the design handoff:
//   ToDo/MidasChat/Claude_Design_files/machineid/design_handoff_midas_v3/
//   chat-window.jsx (line 54) and palette-tokens.css `.cite` atom.
//
// Visual: monospace pill with accent-soft bg + accent-line border + accent
// text. The `.cite` class is shared infrastructure — declared in
// midas-v3-tokens.scss in Phase 1 and inherited here. We expose the same
// class so designers can style it cohesively across the v3 surface.
//
// Click behaviour: dispatches `openArtifactAtPage` (the existing v2 thunk).
// In Phase 2 the v2 artifact dock receives the open call (the v3 dock ships
// in Phase 3). If the bot didn't emit a matching `cross_reference` artifact
// the slice no-ops — matches v2 behaviour.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useCallback } from "react";

import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import { openArtifactAtPage } from "../../midasPageSlice";
import { setArtifactSourceThread } from "../../midasAttachSlice";
import { useAttachColumn } from "./attach/AttachColumnContext";

export interface CiteV3Props {
  /** Page number on the bot-side document to open. Required. */
  page: number;
  /** Optional human-readable label (e.g. "Sheet 175"). Children fall back. */
  label?: string;
  /** Optional accessible tooltip text shown via `title`. */
  tooltip?: string;
  children?: React.ReactNode;
}

const CiteV3: React.FC<CiteV3Props> = ({ page, label, tooltip, children }) => {
  const dispatch = useAppDispatch();
  // MODEL-PICKER P4: which column (null = primary) opened the artifact —
  // drives the "only the source column stays visible" pane choreography.
  const attachCtx = useAttachColumn();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!Number.isFinite(page) || page <= 0) return;
      dispatch(setArtifactSourceThread(attachCtx?.threadId ?? null));
      dispatch(openArtifactAtPage({ page }));
    },
    [dispatch, page, attachCtx]
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      title={tooltip}
      data-cite-page={page}
      // SP-1V (Creative Designer, 2026-07-12): neutral gray source pill to
      // match the §1A reference (the inline `Steam Community` / `ModDB` /
      // `GitHub` evidence pills). CiteV3 sits at the citation position and
      // opens a source — the exact role of those reference pills — so it reads
      // as a quiet gray pill in the body font (mono is reserved for the code
      // chips, per acceptance criterion §8.9). Was: accent-soft fill + accent
      // text + accent border (palette-tokens.css `.cite` atom). Hover/focus
      // affordance lives in midas-v3-tokens.scss (`.midas-v3-cite`). Rendered
      // as <button> so it stays keyboard-focusable + announced as interactive.
      style={{
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "baseline",
        fontFamily: "var(--font-sans)",
        fontSize: 11.5,
        lineHeight: 1.4,
        padding: "1px 8px",
        margin: "0 2px",
        background: "var(--card-2)",
        color: "var(--muted)",
        borderRadius: 7,
        border: "1px solid var(--border)",
        cursor: "pointer",
        whiteSpace: "nowrap",
        fontWeight: 500,
        // Reset default button styling.
        appearance: "none",
        WebkitAppearance: "none",
        // Quiet hover — see `.midas-v3-cite:hover` in midas-v3-tokens.scss.
        transition: "background 0.12s ease, border-color 0.12s ease, color 0.12s ease",
      }}
      className="midas-v3-cite"
    >
      {children ?? label ?? `p.${page}`}
    </button>
  );
};

export default CiteV3;
